import { MSLineProps, Series } from 'components/charts'
import { action, computed, observable } from 'mobx'
import {
  BestProductSalesDTOObject,
  BestProductsDTOObject,
  OrderStatsRequestDTO,
  ProductDTO,
  ProductProjectEventStatDTO,
  ProductProjectSalesDTO,
  ProductTotalPeriodSalesDTO,
  ProductTotalPeriodStatDTO,
  ProjectTotalPeriodSalesDTO,
  ProjectTotalPeriodStatDTO,
  TotalPeriodSales,
  TotalPeriodStats,
  ViewerEventEnums
} from 'models'
import { FilterDTO } from 'models/FilterDTO'
import moment from 'moment'
import { OnSelectCallbackParam } from 'react-daterange-picker'
import { GENERAL_STORE } from 'store'
import { FilterPeriods } from 'store/ActionHistoryTableStore'
import { BRANDS_MAIN_STORE } from 'store/BrandsMainStore'
import { PRODUCTS_MAIN_STORE } from 'store/ProductsMainStore'
import { PROJECT_MAIN_STORE } from 'store/ProjectMainStore'
import { RootStore } from 'store/RootStore'
import { STATISTICS_OVERVIEW_BAR_CHART_STORE } from 'store/StatisticsOverviewBarChartStore'
import { STATISTICS_OVERVIEW_TABLE_STORE } from 'store/StatisticsOverviewTableStore'
import { STATISTICS_PAGE_STORE } from 'store/StatisticsPageStore'
import { Api, getMainImageSrcForObj, getStartAndEndDates, User } from 'utilities'
import {
  OverviewStatTabs,
  ProductProjectStat,
  TotalPeriodData,
  TotalPeriodDataMap
} from './StatisticsOverviewStore.types'
import { DraggableColLabels, getProductProjectStatMapKey } from '../StatsBaseTableStore'

export const STATISTICS_OVERVIEW_STORE = 'STATISTICS_OVERVIEW_STORE'

export class StatisticsOverviewStore {
  private readonly rootStore: RootStore

  constructor (rootStore: RootStore) {
    this.rootStore = rootStore
  }

  // makKey = `${productId}-${projectId}-${brandId}`
  private statsMap: Map<string, ProductProjectStat> = new Map()
  // Map<projectId, Set<statsMapKey>>
  private statsMapKeysByProject: Map<number, Set<string>> = new Map()
  // Map<productId, Set<statsMapKey>>
  private statsMapKeysByProduct: Map<number, Set<string>> = new Map()
  // Map<brandId, Set<statsMapKey>>
  private statsMapKeysByBrand: Map<number, Set<string>> = new Map()

  @observable selectedTab: OverviewStatTabs = OverviewStatTabs.BY_EXPENSES
  @observable periodFilter: FilterPeriods = FilterPeriods.PAST_3_MONTHS
  @observable customPeriod: OnSelectCallbackParam = null
  @observable projectsCount: number = 0
  @observable private totalPeriodDataMap: TotalPeriodDataMap = new Map()
  @observable private bestProducts: BestProductsDTOObject = null
  @observable private bestProductSales: BestProductSalesDTOObject = null

  @action private setPeriodFilter = (periodFilter: FilterPeriods) => {
    this.periodFilter = periodFilter
  }

  @action private setCustomPeriod = (customPeriod: OnSelectCallbackParam) => {
    this.customPeriod = customPeriod
  }

  @action private setSelectedTab = (selectedTab: OverviewStatTabs) => {
    if (selectedTab === OverviewStatTabs.BY_REVENUE) {
      selectedTab = OverviewStatTabs.BY_EXPENSES
    }
    this.selectedTab = selectedTab
  }

  @action private setTotalPeriodDataMap = (
    totalPeriodDataMap: TotalPeriodDataMap
  ) => {
    this.totalPeriodDataMap = totalPeriodDataMap
  }

  @action private setTotalPeriodDataForEntity = (
    entityId: number,
    data: TotalPeriodData
  ): void => {
    this.totalPeriodDataMap.set(entityId, data)
  }

  @action deleteEntityTotalPeriodData = (entityId: number): void => {
    this.totalPeriodDataMap.delete(entityId)
  }

  @action private setProjectsCount = (value: number): void => {
    this.projectsCount = value
  }

  clearTotalPeriodDataMap = (): void => {
    this.setTotalPeriodDataMap(new Map())
  }

  onPeriodFilterSelect = (periodFilter: FilterPeriods) => {
    this.setCustomPeriod(null)
    this.setPeriodFilter(periodFilter)
    this.onPeriodFilterChange()
  }

  onCustomPeriodSelect = (customPeriod: OnSelectCallbackParam) => {
    this.setPeriodFilter(FilterPeriods.CUSTOM)
    this.setCustomPeriod(customPeriod)
    this.onPeriodFilterChange()
  }

  private onPeriodFilterChange = (): void => {
    this.clearTotalPeriodDataMap()
    this.rootStore[STATISTICS_OVERVIEW_BAR_CHART_STORE].getEventsPeriodStats()
    this.rootStore[STATISTICS_OVERVIEW_TABLE_STORE].onOverviewPeriodFilterChange()
    this.getProjectProductEvents()
  }

  onSelectedTabChange = (selectedTab: OverviewStatTabs) => {
    this.setSelectedTab(selectedTab)
    this.rootStore[STATISTICS_OVERVIEW_BAR_CHART_STORE].onStatisticsOverviewTabChange()
    this.rootStore[STATISTICS_OVERVIEW_TABLE_STORE].onStatisticsOverviewTabChange(selectedTab)
  }

  private handleProjectProductEvents = (
    data: [ProductProjectEventStatDTO[], ProductProjectSalesDTO[]]
  ) => {
    const [eventStats, sales] = data

    const handleEachEntity = (entity: ProductProjectEventStatDTO | ProductProjectSalesDTO) => {
      const mapKey = getProductProjectStatMapKey(entity) // `${projectId}-${productId}-${brandId}`

      // add entity to stats Map catalog or update existing ProductProjectStat
      this.statsMap.set(mapKey, {
        ...this.statsMap.get(mapKey),
        ...entity
      })

      // add ProductProjectStat mapKey to its project Set of mapKeys
      if (entity.projectId) {
        this.statsMapKeysByProject.set(
          entity.projectId,
          this.statsMapKeysByProject.get(entity.projectId) || new Set()
        )
        this.statsMapKeysByProject.get(entity.projectId).add(mapKey)
      }

      // add ProductProjectStat mapKey to its product Set of mapKeys
      if (entity.productId) {
        this.statsMapKeysByProduct.set(
          entity.productId,
          this.statsMapKeysByProduct.get(entity.productId) || new Set()
        )
        this.statsMapKeysByProduct.get(entity.productId).add(mapKey)
      }

      // add ProductProjectStat mapKey to its brand Set of mapKeys
      if (entity.brandId) {
        this.statsMapKeysByBrand.set(
          entity.brandId,
          this.statsMapKeysByBrand.get(entity.brandId) || new Set()
        )
        this.statsMapKeysByBrand.get(entity.brandId).add(mapKey)
      }
    }

    // init
    this.statsMap.clear()
    this.statsMapKeysByProject.clear()
    this.statsMapKeysByProduct.clear()
    this.statsMapKeysByBrand.clear()
    this.statsProjectIds.length = 0
    this.statsProductIds.length = 0
    this.setProjectsCount(0)
    // end of init

    eventStats.forEach(handleEachEntity)
    sales.forEach(handleEachEntity)
    this.setProjectsCount(this.statsMapKeysByProject.size)

    // this.setStatsMap(statsMap)
    // this.setProjectProductStatsMap(projectProductStatsMap)
    // this.setProductProjectStatsMap(productProjectStatsMap)

    const {
      [PROJECT_MAIN_STORE]: {
        getProjectsByIds
      },
      [PRODUCTS_MAIN_STORE]: {
        getProducts
      },
      [STATISTICS_OVERVIEW_TABLE_STORE]: {
        refreshColumnFilterSelections,
        triggerSearch,
        setProcessing
      }
    } = this.rootStore

    // clearProjects()
    // clearProjectHierarchyList()

    setProcessing(true)

    Promise.all([
      getProjectsByIds(this.statsProjectIds, {
        loadContents: true,
        loadHierarchy: true
      }),
      getProducts({ ids: this.statsProductIds })
    ])
      .then(() => {
        // set all main entities to be selected in the table tree-view dropdown filter
        refreshColumnFilterSelections()
        // setEntityIdsFilter(new Map([
        //   [DraggableColLabels.PROJECT, new Set(this.statsProjectIds)],
        //   [DraggableColLabels.STORE, new Set(this.statsBrandIds)],
        //   [DraggableColLabels.PRODUCTS, new Set(this.statsProductIds)]
        // ]))
        triggerSearch({ resetPageNumber: true })
      })
  }

  getProjectProductEvents = async () => {
    const {
      [GENERAL_STORE]: {
        setLoadingOff,
        handleApiError
      },
      [STATISTICS_PAGE_STORE]: {
        selectedStoreIds
      },
      [STATISTICS_OVERVIEW_TABLE_STORE]: {
        setProcessing
      }
    } = this.rootStore

    setProcessing(true)

    await Promise.all([
      Api.post<ProductProjectEventStatDTO[], FilterDTO>(
        '/stats/summary/projectProductEvents',
        {
          ...this.period,
          brandIds: selectedStoreIds,
          brandId: null,
          projectIds: null // if projectIds than brandId != null
        },
        'statsApi'
      ),
      Api.post<ProductProjectSalesDTO[], OrderStatsRequestDTO>(
        '/order/statistic/orderProduct',
        {
          startDate: moment(this.period.startDate != null ? this.period.startDate : moment()).format('YYYY-MM-DD'),
          endDate: moment(this.period.endDate != null ? this.period.endDate : moment()).format('YYYY-MM-DD'),
          brands: selectedStoreIds
        }
      )
    ])
      .then(data => {
        this.handleProjectProductEvents(data)
        setLoadingOff()
      })
      .catch(handleApiError)
  }

  getStatsFor = (
    searchBy: { projectId?: number, productId?: number, brandId?: number }
  ): ProductProjectStat[] => {
    const {
      projectId,
      productId,
      brandId
    } = searchBy

    let mapKeysSet: Set<string> = new Set()
    if (projectId) {
      const {
        [PROJECT_MAIN_STORE]: {
          doesProjectHasChildren,
          getProjectChildrenIds
        }
      } = this.rootStore

      const addMapKeysForProject = (id: number) => {
        mapKeysSet = new Set([
          ...mapKeysSet,
          ...this.statsMapKeysByProject.get(id) || new Set()
        ])

        if (doesProjectHasChildren(id)) {
          getProjectChildrenIds(id).forEach(addMapKeysForProject)
        }
      }

      addMapKeysForProject(projectId)
    }
    if (productId) {
      const mapKeysForProduct = this.statsMapKeysByProduct.get(productId)
      if (mapKeysSet.size) {
        mapKeysSet = new Set([
          ...[...mapKeysSet].filter(mapKey => mapKeysForProduct.has(mapKey))
        ])
      } else {
        mapKeysSet = mapKeysForProduct
      }
    }
    if (brandId) {
      const mapKeysForBrand = this.statsMapKeysByBrand.get(brandId)
      if (mapKeysSet.size) {
        mapKeysSet = new Set([
          ...[...mapKeysSet].filter(mapKey => mapKeysForBrand.has(mapKey))
        ])
      } else {
        mapKeysSet = mapKeysForBrand
      }
    }

    const mapKeys: string[] = Array.from((mapKeysSet || new Set()).values())

    return mapKeys.map(mapKey => this.statsMap.get(mapKey))
  }

  getBrandIdsForProject = (projectId: number): Set<number> => {
    const brandIds: Set<number> = new Set()
    const mapKeysForProject = this.statsMapKeysByProject.get(projectId)
    mapKeysForProject.forEach(mapKey => {
      try {
        const brandId = Number(mapKey.split('-').pop())
        !Number.isNaN(brandId) && brandIds.add(brandId)
      } catch (error) {
        console.error(error)
      }
    })
    return brandIds
  }

  getBrandIdsForProduct = (productId: number): Set<number> => {
    const brandId = this.rootStore[PRODUCTS_MAIN_STORE].getProductBrandId(productId)
    return new Set([brandId])
  }

  getProductIdsForProject = (projectId: number): Set<number> => {
    const productIds: Set<number> = new Set()
    const mapKeysForProject = this.statsMapKeysByProject.get(projectId)
    mapKeysForProject.forEach(mapKey => {
      try {
        const productId = Number(mapKey.split('-')[0])
        !Number.isNaN(productId) && productIds.add(productId)
      } catch (error) {
        console.error(error)
      }
    })
    return productIds
  }

  getProjectIdsForBrand = (brandId: number): Set<number> => {
    const projectIds: Set<number> = new Set()
    const mapKeysForBrand = this.statsMapKeysByBrand.get(brandId)
    mapKeysForBrand.forEach(mapKey => {
      try {
        const projectId = Number(mapKey.split('-')[1])
        !Number.isNaN(projectId) && projectIds.add(projectId)
      } catch (error) {
        console.error(error)
      }
    })
    return projectIds
  }

  getProductIdsForBrand = (brandId: number): Set<number> => {
    const productIds: Set<number> = new Set()
    const mapKeysForBrand = this.statsMapKeysByBrand.get(brandId)
    mapKeysForBrand.forEach(mapKey => {
      try {
        const productId = Number(mapKey.split('-')[0])
        !Number.isNaN(productId) && productIds.add(productId)
      } catch (error) {
        console.error(error)
      }
    })
    return productIds
  }

  getProjectIdsForProduct = (productId: number): Set<number> => {
    const projectIds: Set<number> = new Set()
    const mapKeysForProduct = this.statsMapKeysByProduct.get(productId)
    mapKeysForProduct.forEach(mapKey => {
      try {
        const projectId = Number(mapKey.split('-')[1])
        !Number.isNaN(projectId) && projectIds.add(projectId)
      } catch (error) {
        console.error(error)
      }
    })
    return projectIds
  }

  private handleTotalPeriodStatsAndSalesData = (
    entityId: number,
    data: [ProjectTotalPeriodStatDTO[], ProjectTotalPeriodSalesDTO[]] |
    [ProductTotalPeriodStatDTO[], ProductTotalPeriodSalesDTO[]]
  ) => {
    const parsedData: TotalPeriodData = {
      sales: {},
      periodStats: {}
    }

    const [stats, sales] = data as [TotalPeriodStats[], TotalPeriodSales[]]
    stats.forEach(stat => {
      Object.entries(stat.periodStats).forEach(([timeLabel, periodStat]) => {
        const prevValue = parsedData.periodStats[timeLabel] || {
          count: 0,
          revenue: 0,
          totalInteraction: 0,
          totalWatchTime: 0
        }
        parsedData.periodStats[timeLabel] = {
          ...periodStat,
          count: prevValue.count + periodStat.count,
          revenue: prevValue.revenue + periodStat.revenue,
          totalInteraction: prevValue.totalInteraction + periodStat.totalInteraction,
          totalWatchTime: prevValue.totalWatchTime + periodStat.totalWatchTime
        }
      })
    })
    sales.forEach(sale => {
      Object.entries(sale.sales).forEach(([timeLabel, salesDto]) => {
        const prevValue = parsedData.sales[timeLabel] || {
          addToCart: 0,
          commission: 0,
          count: 0,
          total: 0
        }
        parsedData.sales[timeLabel] = {
          ...salesDto,
          addToCart: prevValue.addToCart + salesDto.addToCart,
          commission: prevValue.commission + salesDto.commission,
          count: prevValue.count + salesDto.count,
          total: prevValue.total + salesDto.total
        }
      })
    })

    this.setTotalPeriodDataForEntity(entityId, parsedData)
  }

  getProjectPeriodStats = async (projectId: number): Promise<void> => {
    const {
      [GENERAL_STORE]: {
        setLoadingOn,
        setLoadingOff,
        handleApiError
      },
      [STATISTICS_OVERVIEW_TABLE_STORE]: {
        getSelectedCorrespondingEntitySets
      }
    } = this.rootStore

    setLoadingOn()

    const correspondingEntitySets = getSelectedCorrespondingEntitySets(projectId)
    const hasChildren = Boolean(correspondingEntitySets[DraggableColLabels.PROJECT].size)
    let projectIds = [projectId]

    if (hasChildren) {
      projectIds = projectIds.concat(
        [...correspondingEntitySets[DraggableColLabels.PROJECT].values()]
      )
    }

    const productIds = [...correspondingEntitySets[DraggableColLabels.PRODUCTS].values()]

    await Promise.all([
      Api.post<ProjectTotalPeriodStatDTO[], FilterDTO>(
        '/stats/summary/totalPeriod/projects',
        {
          projectIds,
          productIds,
          ...this.period
          // eventTypes
        },
        'statsApi'
      ),
      Api.post<ProjectTotalPeriodSalesDTO[], OrderStatsRequestDTO>(
        '/order/statistic/period/projects',
        {
          projects: projectIds,
          products: productIds,
          startDate: moment(this.period.startDate != null ? this.period.startDate : moment()).format('YYYY-MM-DD'),
          endDate: moment(this.period.endDate != null ? this.period.endDate : moment()).format('YYYY-MM-DD')
        }
      )
    ])
      .then(data => {
        this.handleTotalPeriodStatsAndSalesData(projectId, data)
        setLoadingOff()
      })
      .catch(handleApiError)
  }

  getProductPeriodStats = async (entityId: number): Promise<void> => {
    const {
      [GENERAL_STORE]: {
        setLoadingOn,
        setLoadingOff,
        handleApiError
      },
      [STATISTICS_OVERVIEW_TABLE_STORE]: {
        getSelectedCorrespondingEntitySets,
        isMainEntityStore,
        selectedViewerEventsToShowArr,
        isViewerEventShown
      }
    } = this.rootStore

    setLoadingOn()

    const correspondingEntitySets = getSelectedCorrespondingEntitySets(entityId)
    const productIds = isMainEntityStore
      ? [...correspondingEntitySets[DraggableColLabels.PRODUCTS].values()]
      : [entityId]
    const projectIds = [...correspondingEntitySets[DraggableColLabels.PROJECT].values()]
    const salesSelected = isViewerEventShown(ViewerEventEnums.PURCHASE) ||
      isViewerEventShown(ViewerEventEnums.ADD_TO_CART)
    const hasEventsSelected = isViewerEventShown(ViewerEventEnums.BLOB_TRIGGERED) ||
      isViewerEventShown(ViewerEventEnums.PRODUCT_PAGE_OPENED) ||
      isViewerEventShown(ViewerEventEnums.POSTER_CLICKED) ||
      isViewerEventShown(ViewerEventEnums.VOV_CLICKED)

    await Promise.all([
      hasEventsSelected
        ? Api.post<ProductTotalPeriodStatDTO[], FilterDTO>(
          '/stats/summary/totalPeriod/products',
          {
            productIds,
            projectIds,
            eventTypes: selectedViewerEventsToShowArr,
            ...this.period
          },
          'statsApi'
        ) : Promise.resolve([] as ProductTotalPeriodStatDTO[]),
      salesSelected
        ? Api.post<ProductTotalPeriodSalesDTO[], OrderStatsRequestDTO>(
          '/order/statistic/period/products',
          {
            products: productIds,
            projects: projectIds,
            startDate: moment(this.period.startDate != null ? this.period.startDate : moment()).format('YYYY-MM-DD'),
            endDate: moment(this.period.endDate != null ? this.period.endDate : moment()).format('YYYY-MM-DD')
          }
        )
        : Promise.resolve([] as ProductTotalPeriodSalesDTO[])
    ])
      .then(data => {
        this.handleTotalPeriodStatsAndSalesData(entityId, data)
        setLoadingOff()
      })
      .catch(handleApiError)
  }

  get statsProjectIds (): number[] {
    return Array.from(this.statsMapKeysByProject.keys())
  }

  get statsBrandIds (): number[] {
    return Array.from(this.statsMapKeysByBrand.keys())
  }

  get statsProductIds (): number[] {
    return Array.from(this.statsMapKeysByProduct.keys())
  }

  get period (): { startDate: number, endDate: number } {
    return getStartAndEndDates(this.customPeriod, this.periodFilter)
  }

  @computed get isByExpensesTabSelected (): boolean {
    return this.selectedTab === OverviewStatTabs.BY_EXPENSES
  }

  @computed get totalPeriodDataEntityIds (): number[] {
    return [...this.totalPeriodDataMap.keys()]
  }

  @computed get dayLabelKeys (): { label: string }[] {
    const daysCountForSelectedPeriod = moment(this.period.endDate).diff(moment(this.period.startDate), 'days')
    if (!daysCountForSelectedPeriod) return null

    const enumerateDaysBetweenDates = (startDate, endDate) => {
        startDate = moment(startDate);
        endDate = moment(endDate);

        let now = startDate, dates: { label: string }[] = [];

        while (now.isBefore(endDate) || now.isSame(endDate)) {
            dates.push({
              label: now.format('DD/MM/YYYY')
            });
            now.add(1, 'days');
        }
        return dates;
    };

    const dayLabelKeys = enumerateDaysBetweenDates(this.period.startDate, this.period.endDate)
    
    return dayLabelKeys
  }

  @computed get periodChartProps (): MSLineProps {
    if (!this.totalPeriodDataMap.size || !this.dayLabelKeys) return null

    const {
      [PROJECT_MAIN_STORE]: {
        getProjectById
      },
      [BRANDS_MAIN_STORE]: {
        getBrandById
      },
      [PRODUCTS_MAIN_STORE]: {
        getProductById
      },
      [STATISTICS_OVERVIEW_TABLE_STORE]: {
        isMainEntityStore,
        isMainEntityProduct,
        isMainEntityProject,
        getEntityGraphColor,
        selectedViewerEventsToShowArr,
        getEventLabel,
        isViewerEventShown
      }
    } = this.rootStore

    const series: Series[] = []

    Array.from(this.totalPeriodDataMap.entries())
      .forEach(([entityId, { periodStats, sales }]) => {
        const data = []
        const entityDTO = (() => {
          switch (true) {
            case isMainEntityProject: return getProjectById(entityId)
            case isMainEntityStore: return getBrandById(entityId)
            case isMainEntityProduct: return getProductById(entityId)
            default: return null
          }
        })()

        this.dayLabelKeys.forEach(({ label }) => {
          let value
          if (this.isByExpensesTabSelected) {
            value = (periodStats[label]?.revenue || 0) + (sales[label]?.commission || 0)
          } else {
            value = (periodStats[label]?.count || 0)
            if (isViewerEventShown(ViewerEventEnums.PURCHASE)) {
              value += sales[label]?.count || 0
            }
            if (isViewerEventShown(ViewerEventEnums.ADD_TO_CART)) {
              value += sales[label]?.addToCart || 0
            }
          }
          data.push({ value })
        })

        series.push({
          seriesname: entityDTO?.name,
          anchorBgColor: getEntityGraphColor(entityId),
          color: getEntityGraphColor(entityId),
          image: isMainEntityProduct
            ? getMainImageSrcForObj(entityDTO)
            : undefined,
          data
        })
      })
    let best: Series = null
    if (selectedViewerEventsToShowArr && (this.bestProducts || this.bestProductSales)) {
      const data = []
      this.dayLabelKeys.forEach(({ label }) => {
        let bestProductId: number = null
        let value = null
        if (!isViewerEventShown(ViewerEventEnums.PURCHASE) &&
          !isViewerEventShown(ViewerEventEnums.ADD_TO_CART) && this.bestProducts) {
          if (this.isByExpensesTabSelected) {
            value = (this.bestProducts[label]?.bestRevenue?.revenue || 0)
            bestProductId = this.bestProducts[label]?.bestRevenue?.productId
          } else {
            value = (this.bestProducts[label]?.bestCount?.eventCount || 0)
            bestProductId = this.bestProducts[label]?.bestCount?.productId
          }
        } else if (this.bestProductSales) {
          if (selectedViewerEventsToShowArr.length === 1 &&
            isViewerEventShown(ViewerEventEnums.PURCHASE)) {
            if (this.isByExpensesTabSelected) {
              value = this.bestProductSales[label]?.topCommission?.sales?.commission
              bestProductId = this.bestProductSales[label]?.topCommission?.productId
            } else {
              value = this.bestProductSales[label]?.topOrdered?.sales?.count
              bestProductId = this.bestProductSales[label]?.topOrdered?.productId
            }
          } else if (!this.isByExpensesTabSelected &&
            selectedViewerEventsToShowArr.length === 1 &&
            isViewerEventShown(ViewerEventEnums.ADD_TO_CART)) {
            value = this.bestProductSales[label]?.topCart?.sales?.addToCart
            bestProductId = this.bestProductSales[label]?.topCart?.productId
          }
        }
        const entityObject: ProductDTO = getProductById(bestProductId)
        data.push({
          value,
          entityName: entityObject?.name,
          entityImage: getMainImageSrcForObj(entityObject)
        })
      })

      best = {
        seriesname: null,
        isBest: true,
        anchorBorderColor: '#B9BBC9',
        color: '#ffffff',
        alpha: '0.1',
        anchorBgColor: '#ffffff',
        data
      }
    }
    const revenueLabel = User.isShopOwner() ? 'Expenses' : 'Revenue'
    const legendLabel = this.isByExpensesTabSelected ? revenueLabel : 'Interactions'

    return {
      yAxisName: this.isByExpensesTabSelected ? 'Expenses USD' : 'INTERACTIONS',
      numberPrefix: this.isByExpensesTabSelected ? '$' : '',
      dataset: series,
      categories: [
        { category: this.dayLabelKeys }
      ],
      best,
      eventsLegend: `${legendLabel}: ${selectedViewerEventsToShowArr.map(getEventLabel).join(', ')}`
    }
  }

  getBestProducts = async (): Promise<void> => {
    const {
      [GENERAL_STORE]: {
        handleApiError
      },
      [STATISTICS_OVERVIEW_TABLE_STORE]: {
        selectedViewerEventsToShowArr,
        isViewerEventShown
      },
      [STATISTICS_PAGE_STORE]: {
        selectedStoreIds
      }
    } = this.rootStore
    // console.log('best products getting ', selectedViewerEventsToShowArr)
    const salesSelected = isViewerEventShown(ViewerEventEnums.PURCHASE) ||
      isViewerEventShown(ViewerEventEnums.ADD_TO_CART)
    if (selectedViewerEventsToShowArr.length && !salesSelected) {
      await Api.post<BestProductsDTOObject, FilterDTO>(
        '/stats/summary/products/best',
        {
          eventTypes: selectedViewerEventsToShowArr,
          ...this.period,
          brandIds: selectedStoreIds
        },
        'statsApi'
      ).then(this.setBestProducts)
        .catch(handleApiError)
    } else if (salesSelected && selectedViewerEventsToShowArr.length === 1) {
      await Api.post<BestProductSalesDTOObject, OrderStatsRequestDTO>(
        '/order/statistic/period/products/best',
        {
          brands: selectedStoreIds,
          startDate: moment(this.period.startDate != null ? this.period.startDate : moment()).format('YYYY-MM-DD'),
          endDate: moment(this.period.endDate != null ? this.period.endDate : moment()).format('YYYY-MM-DD')
        }
      ).then(this.setBestSales)
        .catch(handleApiError)
    }
  }

  @action
  private setBestProducts = (data: BestProductsDTOObject) => {
    this.bestProducts = data
  }

  @action
  private setBestSales = (data: BestProductSalesDTOObject) => {
    this.bestProductSales = data
  }

  @computed
  get hasBest (): boolean {
    return !(!this.bestProducts && !this.bestProductSales)
  }

  getOverviewTabs (): OverviewStatTabs[] {
    return [
      User.isShopOwner()
        ? OverviewStatTabs.BY_EXPENSES
        : OverviewStatTabs.BY_REVENUE,
      OverviewStatTabs.BY_INTERACTION
    ]
  }
}
